import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveIcon from "@mui/icons-material/Remove";
import type {
  AutocompleteClassKey,
  ClassNameMap,
  ChipProps as MuiChipProps,
} from "@mui/material";
import { Collapse } from "@mui/material";
import clsx from "clsx";
import type { FormikValues } from "formik";
import { FieldArray, useField, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "@citrine/atoms/button";
import { Tooltip } from "@citrine/atoms/tooltip";
import { getLookupMap } from "@citrine/utils/helpers";
import type { Off, On, Toggle } from "@citrine/utils/types";

import type {
  ComboboxCheckListProps,
  ComboboxOption,
  ComboboxProps,
  FreeSoloOption,
} from "./combobox";
import { Combobox, getLabel, getValue, isFreeSoloOption } from "./combobox";
import * as sx from "./combobox-with-chips.sx";
import { useFormConfiguration } from "./form";

const DELETE_ICON = <ClearIcon data-testid="chip-delete-icon" />;

type ComboboxWithChipsClassKey =
  | AutocompleteClassKey
  | "chip"
  | "chipsContainer"
  | "container";

export type ChipProps = Pick<MuiChipProps, "className" | "style"> & {
  "data-testid"?: string;
  disableChipDelete?: boolean;
  onChipDelete?: <ValueType extends string = string>(value: ValueType) => void;
};

export type ComboboxWithChipsProps<
  Shape extends FormikValues = FormikValues,
  ValueType extends string = string,
  FreeSolo extends Toggle = Toggle,
> = {
  chipProps?: ChipProps;
  classes?: Partial<ClassNameMap<ComboboxWithChipsClassKey>>;
} & Omit<
  ComboboxProps<Shape, ValueType, On, On, FreeSolo>,
  "disableClearable" | "multiple" | "noTags" | "classes"
> &
  ComboboxCheckListProps<ValueType>;

const getChipValue = (value: string | FreeSoloOption) =>
  isFreeSoloOption(value) ? value.value : value;

export function ComboboxWithChips<
  Shape extends FormikValues = FormikValues,
  ValueType extends string = string,
>({
  chipProps = {},
  className,
  classes: inputClasses = {},
  name,
  limitTags = 15,
  ...props
}:
  | ComboboxWithChipsProps<Shape, ValueType, On>
  | ComboboxWithChipsProps<Shape, ValueType, Off>) {
  const [{ value: selectedValues }, { error }, { setTouched }] = useField<
    ValueType[] | undefined
  >(name);

  const { isSubmitting } = useFormikContext();
  const { readonly } = useFormConfiguration();
  const disabled = props.disabled || readonly;

  const valueMap = useMemo(
    () => getLookupMap(props.options, (option) => getValue<ValueType>(option)),
    [props.options],
  );

  const {
    chip: chipClass,
    chipsContainer: chipsClass,
    container: containerClass,
    ...muiClasses
  } = inputClasses;

  const [overflowExpanded, setOverflowExpanded] = useState(false);
  const showOverflow = useCallback(
    () => setOverflowExpanded(true),
    [setOverflowExpanded],
  );
  const hideOverflow = useCallback(
    () => setOverflowExpanded(false),
    [setOverflowExpanded],
  );
  const overflowCount = useMemo(
    () => (selectedValues ? selectedValues?.length - limitTags : 0),
    [limitTags, selectedValues],
  );

  useEffect(() => {
    if (overflowCount === 0 && overflowExpanded) {
      hideOverflow();
    }
  }, [hideOverflow, overflowCount, overflowExpanded]);

  const ComboboxChip = useCallback(
    ({
      index,
      onDelete,
      value,
    }: {
      index: number;
      onDelete?: () => void;
      value: string;
    }) => {
      const errorMessage = Array.isArray(error) ? error[index] : undefined;

      return (
        <Tooltip error={errorMessage} title={value}>
          <sx.Chip
            data-testid={chipProps["data-testid"]}
            className={clsx(chipClass, chipProps.className)}
            color={errorMessage ? "error" : undefined}
            disabled={isSubmitting}
            deleteIcon={disabled ? undefined : DELETE_ICON}
            label={getLabel<ValueType>(
              valueMap[value] ?? (value as ComboboxOption<ValueType>),
            )}
            style={chipProps.style}
            onDelete={
              disabled || chipProps.disableChipDelete
                ? undefined
                : () => {
                    onDelete?.();
                    chipProps.onChipDelete?.(value);
                  }
            }
            size="small"
          />
        </Tooltip>
      );
    },
    [chipClass, chipProps, disabled, error, isSubmitting, valueMap],
  );

  return (
    <sx.Container className={clsx(className, containerClass)}>
      <Combobox<Shape, ValueType>
        {...props}
        classes={muiClasses}
        disableClearable
        multiple
        name={name}
        noTags
      />

      <FieldArray
        name={name}
        render={({ remove }) => {
          const removeAndSetTouched = (index: number) => {
            remove(index);
            setTouched(true);
          };
          return (
            <sx.ChipsContainer className={chipsClass}>
              {limitTags === -1 // per Material UI docs, -1 disables limit tags, respect that if passed in
                ? selectedValues?.map((value, index) => (
                    <ComboboxChip
                      key={getChipValue(value)}
                      index={index}
                      onDelete={() => removeAndSetTouched(index)}
                      value={getChipValue(value)}
                    />
                  ))
                : selectedValues
                    ?.slice(0, limitTags)
                    .map((value, index) => (
                      <ComboboxChip
                        key={getChipValue(value)}
                        index={index}
                        onDelete={() => removeAndSetTouched(index)}
                        value={getChipValue(value)}
                      />
                    ))}

              {limitTags === -1 ? null : (
                <Collapse in={overflowExpanded}>
                  {selectedValues?.slice(limitTags).map((value, index) => (
                    <ComboboxChip
                      key={getChipValue(value)}
                      index={limitTags + index}
                      onDelete={() => removeAndSetTouched(limitTags + index)}
                      value={getChipValue(value)}
                    />
                  ))}
                </Collapse>
              )}
            </sx.ChipsContainer>
          );
        }}
      />

      {limitTags === -1 ? null : overflowCount > 0 ? (
        overflowExpanded ? (
          <Button
            data-testid="hide-overflow"
            onClick={hideOverflow}
            size="small"
            startIcon={<RemoveIcon />}
            variant="text"
          >
            <FormattedMessage
              id="components.combobox.hide {count}"
              values={{ count: overflowCount }}
            />
          </Button>
        ) : (
          <Button
            data-testid="show-overflow"
            onClick={showOverflow}
            size="small"
            startIcon={<AddIcon />}
            variant="text"
          >
            <FormattedMessage
              id="components.combobox.show {count} more"
              values={{ count: overflowCount }}
            />
          </Button>
        )
      ) : null}
    </sx.Container>
  );
}
